import React, { Suspense } from 'react';
import { createBrowserRouter, Navigate, useRouteError } from 'react-router-dom';
import Shop from './Shop';
import ShopApplicationWrapper from './pages/ShopApplicationWrapper';
import { loadProductBySlug } from './routes/products';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute/AdminProtectedRoute';
import StaffProtectedRoute from './components/StaffProtectedRoute/StaffProtectedRoute';
import ProductLoadError from './pages/ProductDetailPage/ProductLoadError';
import { lazyWithRetry } from './lazyWithRetry';

// Lazy-loaded page chunks — each route gets its own JS chunk (smaller initial bundle).
// lazyWithRetry auto-reloads once on ChunkLoadError so stale-cache deploys recover
// transparently instead of dumping the React Router error screen on the shopper.
const ProductListPage = lazyWithRetry(
  () => import('./pages/ProductListPage/ProductListPage'),
  'ProductListPage'
);
const ProductDetails = lazyWithRetry(
  () => import('./pages/ProductDetailPage/ProductDetails'),
  'ProductDetails'
);
const Cart = lazyWithRetry(() => import('./pages/Cart/Cart'), 'Cart');
const Favorites = lazyWithRetry(() => import('./pages/Favorites/Favorites'), 'Favorites');
const Account = lazyWithRetry(() => import('./pages/Account/Account'), 'Account');
const Profile = lazyWithRetry(() => import('./pages/Account/Profile'), 'Profile');
const Orders = lazyWithRetry(() => import('./pages/Account/Orders'), 'Orders');
const Settings = lazyWithRetry(() => import('./pages/Account/Settings'), 'Settings');
const Checkout = lazyWithRetry(() => import('./pages/Checkout/Checkout'), 'Checkout');
const ConfirmPayment = lazyWithRetry(
  () => import('./pages/ConfirmPayment/ConfirmPayment'),
  'ConfirmPayment'
);
const OrderReview = lazyWithRetry(
  () => import('./pages/OrderConfirmed/OrderConfirmed'),
  'OrderReview'
);
const Contacto = lazyWithRetry(() => import('./pages/Contacto/Contacto'), 'Contacto');
const PreguntasFrecuentes = lazyWithRetry(
  () => import('./pages/PreguntasFrecuentes/PreguntasFrecuentes'),
  'PreguntasFrecuentes'
);
const PaymentMethods = lazyWithRetry(
  () => import('./pages/PaymentMethods/PaymentMethods'),
  'PaymentMethods'
);
const CollectionsIndex = lazyWithRetry(
  () => import('./pages/Collections/CollectionsIndex'),
  'CollectionsIndex'
);
const CollectionDetail = lazyWithRetry(
  () => import('./pages/Collections/CollectionDetail'),
  'CollectionDetail'
);
const CmsPage = lazyWithRetry(() => import('./pages/Page/Page'), 'CmsPage');

// Auth pages
const AuthenticationWrapper = lazyWithRetry(
  () => import('./pages/AuthenticationWrapper'),
  'AuthenticationWrapper'
);
const Login = lazyWithRetry(() => import('./pages/Login/Login'), 'Login');
const Register = lazyWithRetry(() => import('./pages/Register/Register'), 'Register');
const ForgotPassword = lazyWithRetry(
  () => import('./pages/ForgotPassword/ForgotPassword'),
  'ForgotPassword'
);
const ResetPassword = lazyWithRetry(
  () => import('./pages/ResetPassword/ResetPassword'),
  'ResetPassword'
);
const OAuth2LoginCallback = lazyWithRetry(
  () => import('./pages/OAuth2LoginCallback'),
  'OAuth2LoginCallback'
);
const NotFound = lazyWithRetry(() => import('./pages/NotFound/NotFound'), 'NotFound');

/** react-admin is large — load only when an admin opens /admin. */
const StaffApp = lazyWithRetry(() => import('./pages/Staff/StaffApp'), 'StaffApp');
const AdminPanel = lazyWithRetry(
  () => import('./pages/AdminPanel/AdminPanel').then((m) => ({ default: m.AdminPanel })),
  'AdminPanel'
);

/**
 * Root error boundary — catches anything the lazy retry didn't, including
 * chunk failures on the SECOND attempt (genuine network issues) and any
 * unexpected runtime error inside a route element. Default react-router
 * screen is dev-oriented; we replace it with a friendly Spanish recovery UI.
 */
const RootErrorBoundary = () => {
  const error = useRouteError();
  const msg = `${error?.name || ''} ${error?.message || ''}`;
  const isChunkErr =
    /ChunkLoadError|Loading chunk|dynamically imported module|Importing a module script failed/i.test(
      msg
    );
  // One more reload attempt for chunk errors when our sessionStorage marker
  // wasn't set (e.g. the error was thrown by the Suspense boundary itself).
  React.useEffect(() => {
    if (!isChunkErr) return;
    let retried = false;
    try {
      retried = sessionStorage.getItem('pinksisly:chunk-retry:root') === '1';
    } catch {}
    if (retried) return;
    try {
      sessionStorage.setItem('pinksisly:chunk-retry:root', '1');
    } catch {}
    window.location.reload();
  }, [isChunkErr]);
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FAF6F1',
        padding: 24,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div style={{ maxWidth: 420, textAlign: 'center', color: '#2A1F1A' }}>
        <h1
          style={{
            fontSize: 22,
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: 'italic',
            margin: '0 0 12px',
          }}
        >
          Pinksisly
        </h1>
        <p style={{ fontSize: 15, color: '#6B5950', margin: '0 0 20px' }}>
          {isChunkErr
            ? 'Estamos actualizando la tienda. Volvé a cargar la página para ver la versión más reciente.'
            : 'Algo salió mal. Probá recargar la página o volvé al inicio.'}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 22px',
              borderRadius: 6,
              border: 'none',
              background: '#2A1F1A',
              color: '#FAF6F1',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Recargar
          </button>
          <a
            href="/"
            style={{
              padding: '10px 22px',
              borderRadius: 6,
              border: '1px solid #E8DDD2',
              background: '#fff',
              color: '#2A1F1A',
              fontSize: 14,
              fontWeight: 500,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
};

const pageFallback = (
  <div className="min-h-screen flex items-center justify-center bg-bg-base">
    <div className="w-8 h-8 border-2 border-line-soft border-t-accent-strong rounded-full animate-spin" />
  </div>
);

const adminFallback = (
  <div className="min-h-screen flex items-center justify-center bg-white text-gray-600 text-sm">
    Cargando panel…
  </div>
);

export const router = createBrowserRouter([
  {
    path: '/admin/*',
    errorElement: <RootErrorBoundary />,
    element: (
      <Suspense fallback={adminFallback}>
        <AdminProtectedRoute>
          <AdminPanel />
        </AdminProtectedRoute>
      </Suspense>
    ),
  },
  {
    path: '/staff/*',
    errorElement: <RootErrorBoundary />,
    element: (
      <Suspense fallback={adminFallback}>
        <StaffProtectedRoute>
          <StaffApp />
        </StaffProtectedRoute>
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <ShopApplicationWrapper />,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        path: '/',
        element: <Shop />,
      },
      {
        path: '/ropas',
        element: (
          <Suspense fallback={pageFallback}>
            <ProductListPage categoryType={'ROPAS'} />
          </Suspense>
        ),
      },
      {
        path: '/product/:slug',
        loader: loadProductBySlug,
        element: (
          <Suspense fallback={pageFallback}>
            <ProductDetails />
          </Suspense>
        ),
        errorElement: <ProductLoadError />,
      },
      {
        path: '/cart-items',
        element: (
          <ProtectedRoute>
            <Suspense fallback={pageFallback}>
              <Cart />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/favorites',
        element: (
          <ProtectedRoute>
            <Suspense fallback={pageFallback}>
              <Favorites />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/account-details/',
        element: (
          <ProtectedRoute>
            <Suspense fallback={pageFallback}>
              <Account />
            </Suspense>
          </ProtectedRoute>
        ),
        children: [
          {
            path: 'profile',
            element: (
              <ProtectedRoute>
                <Suspense fallback={pageFallback}>
                  <Profile />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'orders',
            element: (
              <ProtectedRoute>
                <Suspense fallback={pageFallback}>
                  <Orders />
                </Suspense>
              </ProtectedRoute>
            ),
          },
          {
            path: 'settings',
            element: (
              <ProtectedRoute>
                <Suspense fallback={pageFallback}>
                  <Settings />
                </Suspense>
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: '/checkout',
        element: (
          <ProtectedRoute>
            <Suspense fallback={pageFallback}>
              <Checkout />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/orderConfirmed',
        element: (
          <Suspense fallback={pageFallback}>
            <OrderReview />
          </Suspense>
        ),
      },
      {
        path: '/order-review',
        element: (
          <Suspense fallback={pageFallback}>
            <OrderReview />
          </Suspense>
        ),
      },
      {
        path: '/contacto',
        element: (
          <Suspense fallback={pageFallback}>
            <Contacto />
          </Suspense>
        ),
      },
      // Legacy uppercase redirect — keeps old links working
      {
        path: '/Contacto',
        element: (
          <Suspense fallback={pageFallback}>
            <Contacto />
          </Suspense>
        ),
      },
      {
        path: '/preguntas-frecuentes',
        element: (
          <Suspense fallback={pageFallback}>
            <PreguntasFrecuentes />
          </Suspense>
        ),
      },
      {
        path: '/metodos-de-pago',
        element: (
          <Suspense fallback={pageFallback}>
            <PaymentMethods />
          </Suspense>
        ),
      },
      // Legacy uppercase redirect
      {
        path: '/Metodos-de-Pago',
        element: (
          <Suspense fallback={pageFallback}>
            <PaymentMethods />
          </Suspense>
        ),
      },
      {
        path: '/looks',
        element: <Navigate to="/colecciones" replace />,
      },
      {
        path: '/envios',
        element: <Navigate to="/preguntas-frecuentes" replace />,
      },
      {
        path: '/cambios',
        element: <Navigate to="/preguntas-frecuentes" replace />,
      },
      // ── Phase 4-a Colecciones ──────────────────────────────────
      {
        path: '/colecciones',
        element: (
          <Suspense fallback={pageFallback}>
            <CollectionsIndex />
          </Suspense>
        ),
      },
      {
        path: '/colecciones/:slug',
        element: (
          <Suspense fallback={pageFallback}>
            <CollectionDetail />
          </Suspense>
        ),
      },
      // ── Phase 4-d CMS pages (FAQ, terms, etc.) ─────────────────
      {
        path: '/pagina/:slug',
        element: (
          <Suspense fallback={pageFallback}>
            <CmsPage />
          </Suspense>
        ),
      },
      // ── Catch-all 404 (with layout) ────────────────────────────
      {
        path: '*',
        element: (
          <Suspense fallback={pageFallback}>
            <NotFound />
          </Suspense>
        ),
      },
    ],
  },
  // ── Auth pages — clean URLs (canonical) ─────────────────────────
  {
    path: '/',
    element: (
      <Suspense fallback={pageFallback}>
        <AuthenticationWrapper />
      </Suspense>
    ),
    children: [
      {
        path: 'login',
        element: (
          <Suspense fallback={pageFallback}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: 'register',
        element: (
          <Suspense fallback={pageFallback}>
            <Register />
          </Suspense>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <Suspense fallback={pageFallback}>
            <ForgotPassword />
          </Suspense>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <Suspense fallback={pageFallback}>
            <ResetPassword />
          </Suspense>
        ),
      },
    ],
  },
  // ── Legacy /v1/* redirects → clean URLs (preserve query/hash) ─
  { path: '/v1/login', element: <Navigate to="/login" replace /> },
  { path: '/v1/register', element: <Navigate to="/register" replace /> },
  { path: '/v1/forgot-password', element: <Navigate to="/forgot-password" replace /> },
  { path: '/v1/reset-password', element: <Navigate to="/reset-password" replace /> },
  {
    path: '/oauth2/callback',
    element: (
      <Suspense fallback={pageFallback}>
        <OAuth2LoginCallback />
      </Suspense>
    ),
  },
  {
    path: '/confirmPayment',
    element: (
      <Suspense fallback={pageFallback}>
        <ConfirmPayment />
      </Suspense>
    ),
  },
]);
